/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * ClaimFilingPage
 *
 * Top-level orchestrator for the claims filing experience. It wires routing
 * params and query strings, fetches coverage/tasks, coordinates the current
 * step, and passes state/actions to sub-steps: Review, Call, and LoR.
 * Also manages URL persistence for step and call/task identifiers.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AgentChatProvider } from '@/components/agent-chat/context/AgentChatContext';
import { ReviewSendLorPage } from '@/components/coverage/ReviewSendLorPage';
import { DocumentSelectionWithUpload } from '@/components/postgrid/wizard/DocumentSelectionWithUpload';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useStartEnhancedBlandCallWorkflowMutation } from '@/services/api/blandService';
import { useCreateBlobMutation } from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import { useSendFaxFromBlobMutation } from '@/services/api/faxService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { CoverageCategory, PolicyType } from '@/services/types/coverage-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { ClaimAcknowledgementStep } from './ack/ClaimAcknowledgementStep';
import { ClaimCallStep } from './call/ClaimCallStep';
// Old header is superseded by ClaimFilingTopHeader
import { ClaimFilingStepper } from './common/ClaimFilingStepper';
import { ClaimFilingTopHeader } from './common/ClaimFilingTopHeader';
import { StepActionHeader } from './common/StepActionHeader';
import {
  ClaimFilingStep,
  useClaimFilingState,
} from './hooks/useClaimFilingState';
import { ClaimLorStep } from './lor/ClaimLorStep';
import { OpenPremisesClaimPage } from './premises/OpenPremisesClaimPage';
import { getLatestCoverageTaskByCanonicalType } from './premises/premisesFlow';
import { PremisesStep, PremisesStepper } from './premises/PremisesStepper';
import { ClaimReviewStep } from './review/ClaimReviewStep';
import { useCoverageStepProgress } from './state/useCoverageStepProgress';

const parseClaimFilingStep = (
  value: string | null,
): ClaimFilingStep | null => {
  switch (value) {
    case 'review':
    case 'call':
    case 'lor':
    case 'review_send':
    case 'ack':
      return value;
    default:
      return null;
  }
};

const parsePremisesClaimsReviewStep = (
  value: ClaimFilingStep | null,
): ClaimFilingStep | null => {
  switch (value) {
    case 'lor':
    case 'review_send':
    case 'call':
      return value;
    default:
      return null;
  }
};

const getMutationErrorMessage = (error: unknown): string | null => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return null;
  }
  const { data } = error as { data?: { error?: string; detail?: string } };
  if (typeof data?.error === 'string') {
    return data.error;
  }
  if (typeof data?.detail === 'string') {
    return data.detail;
  }
  return null;
};

export const ClaimFilingPage: React.FC = () => {
  const params = useParams<{ matter_id: string; coverage_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const matterId = params.matter_id as string;
  const coverageIdParam = params.coverage_id as string;
  const stepParam = parseClaimFilingStep(searchParams.get('step'));
  const { data: linkedCoverage } = useGetCoverageQuery(
    coverageIdParam ? { matterId, coverageId: coverageIdParam } : skipToken,
  );
  const { data: tasksResponse } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  }, {
    refetchOnMountOrArgChange: true,
  });
  const [startEnhancedWorkflow, startWorkflowState] =
    useStartEnhancedBlandCallWorkflowMutation();
  const lastStartClickRef = useRef(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [proposedNumber, setProposedNumber] = useState('');
  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);
  const [isBpoMode, setIsBpoMode] = useState(false);
  const [bpoAttachmentBlobIds, setBpoAttachmentBlobIds] = useState<Set<string>>(
    new Set(),
  );

  const { reviewDone, callDone, lorDone, reviewSendDone, ackDone } =
    useCoverageStepProgress(matterId, coverageIdParam);

  // Check if this is a premises coverage
  const isPremisesCoverage =
    linkedCoverage?.category === CoverageCategory.PROPERTY;

  const tasks = useMemo(
    () => tasksResponse?.results || [],
    [tasksResponse?.results],
  );

  const reviewNAResolved = useMemo(() => {
    const find1pNA = tasks.some(
      (t) =>
        t.type?.canonical_type === CanonicalTaskType.FIND_1P_COVERAGE &&
        t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
    );
    const find3pNA = tasks.some(
      (t) =>
        t.type?.canonical_type === CanonicalTaskType.FIND_3P_COVERAGE &&
        t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
    );
    if (linkedCoverage?.category === CoverageCategory.FIRST_PARTY)
      return find1pNA;
    if (linkedCoverage?.category === CoverageCategory.THIRD_PARTY)
      return find3pNA;
    const reviewTask = getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageIdParam,
      CanonicalTaskType.REVIEW_FOUND_COVERAGE,
    );
    return (
      reviewTask?.status?.canonical_status ===
        CanonicalTaskStatus.NOT_APPLICABLE || false
    );
  }, [tasks, linkedCoverage?.category, coverageIdParam]);

  const reviewDoneResolved = useMemo(() => {
    // A task is considered complete if canonical_status is COMPLETED or completed_at is set
    const isTaskCompleted = (t: {
      status?: { canonical_status?: string };
      completed_at?: string | null;
    }) =>
      t.status?.canonical_status === CanonicalTaskStatus.COMPLETED ||
      Boolean(t.completed_at);

    const find1pCompleted = tasks.some(
      (t) =>
        t.type?.canonical_type === CanonicalTaskType.FIND_1P_COVERAGE &&
        isTaskCompleted(t),
    );
    const find3pCompleted = tasks.some(
      (t) =>
        t.type?.canonical_type === CanonicalTaskType.FIND_3P_COVERAGE &&
        isTaskCompleted(t),
    );
    if (linkedCoverage?.category === CoverageCategory.FIRST_PARTY) {
      return find1pCompleted || reviewNAResolved || false;
    }
    if (linkedCoverage?.category === CoverageCategory.THIRD_PARTY) {
      return find3pCompleted || reviewNAResolved || false;
    }
    // Fallback: coverage-specific review task
    const reviewTask = getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageIdParam,
      CanonicalTaskType.REVIEW_FOUND_COVERAGE,
    );
    const reviewCompleted = reviewTask ? isTaskCompleted(reviewTask) : false;
    return reviewCompleted || reviewDone || reviewNAResolved || false;
  }, [
    tasks,
    linkedCoverage?.category,
    coverageIdParam,
    reviewDone,
    reviewNAResolved,
  ]);

  const callNAResolved = useMemo(() => {
    const task = getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageIdParam,
      CanonicalTaskType.OPEN_CLAIM,
    );
    return (
      task?.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE
    );
  }, [tasks, coverageIdParam]);

  const lorNAResolved = useMemo(() => {
    const task = getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageIdParam,
      CanonicalTaskType.SEND_LOR,
    );
    return (
      task?.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE
    );
  }, [tasks, coverageIdParam]);

  const ackNAResolved = useMemo(() => {
    const task = getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageIdParam,
      CanonicalTaskType.LOR_ACKNOWLEDGEMENT,
    );
    return (
      task?.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE
    );
  }, [tasks, coverageIdParam]);

  const callDoneResolved = callDone || callNAResolved || false;
  const lorDoneResolved = lorDone || lorNAResolved || false;
  const reviewSendDoneResolved = reviewSendDone || lorNAResolved || false;
  const ackDoneResolved = ackDone || ackNAResolved || false; // NA counts as done for UI

  const step = useMemo<ClaimFilingStep | null>(() => {
    if (!linkedCoverage?.category) {
      return null;
    }
    if (linkedCoverage.category === CoverageCategory.PROPERTY) {
      return parsePremisesClaimsReviewStep(stepParam) ?? 'lor';
    }
    if (stepParam) {
      return stepParam;
    }
    return 'review';
  }, [linkedCoverage?.category, stepParam]);

  const premisesCurrentStep = useMemo<PremisesStep | null>(() => {
    if (!isPremisesCoverage || !step) {
      return null;
    }
    if (step === 'call') {
      return 'open_premises_claim';
    }
    if (step === 'lor' || step === 'review_send') {
      return step;
    }
    return null;
  }, [isPremisesCoverage, step]);

  const {
    reviewedFields,
    allRequiredReviewed,
    callStatus,
    isCallOutcomeReady,
    // lorArtifact,
    // generatedLors,
    finalizeDetails,
    coverageType,
    setCoverageType,
    markReviewed,
    modifyField,
    updateFinalizeDetail,
    triggerCall,
    // generateLor,
    saveFinalizeDetails,
    requirementsById,
    confirmRequirementValue,
  } = useClaimFilingState({ matterId, coverageId: coverageIdParam });

  const [activeFieldKey, setActiveFieldKey] = useState<string | null>(null);

  const handleGoto = React.useCallback(
    (nextStep: ClaimFilingStep) => {
      const next = new URLSearchParams(searchParams);
      if (next.get('step') === nextStep) {
        return;
      }
      next.set('step', nextStep);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Track if we're simulating a fax send
  const [isSendingFax, setIsSendingFax] = useState(false);

  // Track active fax transmissions
  const [activeFaxTransmissions, setActiveFaxTransmissions] = useState<
    Map<string, string>
  >(new Map()); // Map of lorId to transmissionId
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId, {
    skip: !matterId,
  });
  const [createBlob] = useCreateBlobMutation();
  const [sendFaxFromBlob] = useSendFaxFromBlobMutation();

  // Lightweight manual refresh: invalidate the Enhanced workflows tag via state tick
  useEffect(() => {
    const handler = () => setRefreshTick((t) => t + 1);
    window.addEventListener('claims.call.refresh', handler as EventListener);
    return () =>
      window.removeEventListener(
        'claims.call.refresh',
        handler as EventListener,
      );
  }, []);

  const isThirdPartyAuto =
    linkedCoverage?.category === CoverageCategory.THIRD_PARTY &&
    linkedCoverage?.policy_type === PolicyType.AUTO;

  useEffect(() => {
    if (!linkedCoverage?.category) return;
    const type =
      linkedCoverage.category === CoverageCategory.FIRST_PARTY ? '1P' : '3P';
    setCoverageType(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCoverage?.category]);

  useEffect(() => {
    if (!isThirdPartyAuto) return;
    reviewedFields
      .filter((f) => f.required && !f.isReviewed)
      .forEach((f) => markReviewed(f.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThirdPartyAuto, reviewedFields]);

  const handleTriggerCall = async () => {
    // Start UI flow
    triggerCall();
    handleGoto('call');

    try {
      const openClaimTaskType =
        linkedCoverage?.category === CoverageCategory.PROPERTY
          ? CanonicalTaskType.OPEN_PREMISES_CLAIM
          : CanonicalTaskType.OPEN_CLAIM;
      const openClaimTask = getLatestCoverageTaskByCanonicalType(
        tasks,
        coverageIdParam,
        openClaimTaskType,
      );
      const taskId = openClaimTask?.id;

      if (!taskId) {
        toast.error(
          'No open claim task found for this coverage. You can call manually.',
        );
        return;
      }

      // Compute proposed number from carrier directory and open confirmation modal
      const firstCarrierPhone =
        (linkedCoverage?.carrier?.handles || [])
          .filter(
            (h) => h?.value && h?.handle_type?.toLowerCase().includes('phone'),
          )
          .map((h) => h.value)[0] || '';
      setProposedNumber(firstCarrierPhone);
      setPendingTaskId(taskId);
      setIsBpoMode(false);
      setIsConfirmModalOpen(true);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to start Voice AI call:', err);
      toast.error('Failed to start Voice AI call. You can call manually.');
    }
  };

  const handleTriggerBpo = async () => {
    // Mirror handleTriggerCall but mark BPO mode
    triggerCall();
    handleGoto('call');

    try {
      const openClaimTaskType =
        linkedCoverage?.category === CoverageCategory.PROPERTY
          ? CanonicalTaskType.OPEN_PREMISES_CLAIM
          : CanonicalTaskType.OPEN_CLAIM;
      const openClaimTask = getLatestCoverageTaskByCanonicalType(
        tasks,
        coverageIdParam,
        openClaimTaskType,
      );
      const taskId = openClaimTask?.id;

      if (!taskId) {
        toast.error(
          'No open claim task found for this coverage. You can call manually.',
        );
        return;
      }

      const firstCarrierPhone =
        (linkedCoverage?.carrier?.handles || [])
          .filter(
            (h) => h?.value && h?.handle_type?.toLowerCase().includes('phone'),
          )
          .map((h) => h.value)[0] || '';
      setProposedNumber(firstCarrierPhone);
      setPendingTaskId(taskId);
      setIsBpoMode(true);
      setBpoAttachmentBlobIds(new Set()); // Reset attachments when opening BPO modal
      setIsConfirmModalOpen(true);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to send to BPO:', err);
      toast.error('Failed to prepare BPO request. You can call manually.');
    }
  };

  const handleConfirmStart = async () => {
    if (!pendingTaskId) return;

    // 500ms debounce to prevent accidental double-clicks
    const now = Date.now();
    if (now - lastStartClickRef.current < 500) {
      toast.info('Please wait before retrying');
      return;
    }
    lastStartClickRef.current = now;

    try {
      const result = await startEnhancedWorkflow({
        matterId,
        taskId: pendingTaskId,
        preferredPhoneNumber: proposedNumber || undefined,
        maxCallsOverride: isBpoMode ? 0 : undefined,
        attachmentBlobIds:
          isBpoMode && bpoAttachmentBlobIds.size > 0
            ? Array.from(bpoAttachmentBlobIds)
            : undefined,
      }).unwrap();

      if (result.terminated_existing) {
        toast.success(
          'Existing workflow terminated and new workflow started',
        );
      } else {
        toast.success(
          isBpoMode
            ? 'Request sent to BPO team'
            : 'Voice AI call started successfully',
        );
      }

      setIsConfirmModalOpen(false);
      setIsBpoMode(false);
      setBpoAttachmentBlobIds(new Set());
      handleGoto('call');
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error(
        isBpoMode
          ? 'Failed to start BPO call:'
          : 'Failed to start Voice AI call:',
        err,
      );
      const serverMessage = getMutationErrorMessage(err) ?? '';
      const fallback = isBpoMode
        ? 'Failed to start BPO call.'
        : 'Failed to start Voice AI call.';
      toast.error(
        serverMessage
          ? `${fallback} ${serverMessage}`
          : `${fallback} You can call manually.`,
      );
    }
  };

  const handleConfirmValue = async (requirementId: string, valueId: string) => {
    try {
      await confirmRequirementValue(requirementId, valueId);
      toast.success('Confirmed');
    } catch {
      toast.error('Confirm failed');
    }
  };

  const handleFaxLor = async (lorId: string, faxNumber: string) => {
    try {
      setIsSendingFax(true);
      if (!collectionTree) throw new Error('Collections not loaded');

      // 1) Fetch preview as Blob
      const previewResp = await fetch(
        `/api/matters/${matterId}/documents/${lorId}/preview/`,
        { cache: 'no-store' },
      );
      if (!previewResp.ok) throw new Error('Preview fetch failed');
      const previewBlob = await previewResp.blob();

      // 2) Upload blob to root collection
      const fileName = 'LetterOfRepresentation.pdf';
      const formData = new FormData();
      formData.append('file', previewBlob, fileName);
      formData.append('name', fileName);
      formData.append('original_filename', fileName);
      formData.append('collection', collectionTree.id);
      formData.append('file_mime_type', previewBlob.type || 'application/pdf');

      const uploaded = await createBlob({
        matterId,
        // FormData is the expected type for this endpoint
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        blob: formData as any,
      }).unwrap();

      // 3) Send fax with uploaded blob id
      const tx = await sendFaxFromBlob({
        recipient_number: faxNumber,
        blob_id: uploaded.id,
        matter_id: matterId,
        file_name: fileName,
        document_type: 'letter_of_representation',
      }).unwrap();

      setActiveFaxTransmissions((prev) => {
        const next = new Map(prev);
        next.set(lorId, tx.transmission_id);
        return next;
      });
      toast.success('Fax queued. Tracking status…', { duration: 3000 });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to send fax:', error);
      toast.error('Failed to send fax. Please try again.');
    } finally {
      setIsSendingFax(false);
    }
  };

  const isWorkflowReady =
    Boolean(linkedCoverage) &&
    Boolean(step) &&
    (!isPremisesCoverage || Boolean(premisesCurrentStep));

  const workflowStepper = (() => {
    if (!linkedCoverage || !step) {
      return null;
    }
    if (isPremisesCoverage) {
      if (!premisesCurrentStep) {
        return null;
      }
      return (
        <PremisesStepper
          matterId={matterId}
          coverageId={coverageIdParam}
          currentStep={premisesCurrentStep}
          coverage={linkedCoverage}
          onGoto={(nextStep) => handleGoto(nextStep as ClaimFilingStep)}
        />
      );
    }
    return (
      <ClaimFilingStepper
        step={step}
        onGoto={handleGoto}
        allRequiredReviewed={allRequiredReviewed}
        reviewDone={reviewDoneResolved}
        callDone={callDoneResolved}
        lorDone={lorDoneResolved}
        reviewSendDone={reviewSendDoneResolved}
        ackDone={ackDoneResolved}
        reviewNA={reviewNAResolved}
        callNA={callNAResolved}
        lorNA={lorNAResolved}
        ackNA={ackNAResolved}
        coverage={linkedCoverage}
      />
    );
  })();

  const workflowContent = (() => {
    if (!isWorkflowReady || !linkedCoverage || !step) {
      return (
        <div className="px-4 py-6 text-sm text-zinc-500">
          Loading claim workflow...
        </div>
      );
    }

    return (
      <>
        <StepActionHeader
          step={step}
          matterId={matterId}
          coverageId={coverageIdParam}
        />
        {step === 'review' && (
          <ClaimReviewStep
            matterId={matterId}
            coverageId={coverageIdParam}
            linkedCoverage={linkedCoverage}
            reviewedFields={reviewedFields}
            requirementsById={requirementsById}
            coverageType={coverageType}
            isThirdPartyAuto={!!isThirdPartyAuto}
            activeFieldKey={activeFieldKey}
            setActiveFieldKey={setActiveFieldKey}
            markReviewed={markReviewed}
            modifyField={modifyField}
            onConfirmValue={handleConfirmValue}
          />
        )}

        {step === 'call' &&
          (isPremisesCoverage ? (
            <OpenPremisesClaimPage key={`premises-claim-${coverageIdParam}`} />
          ) : (
            <ClaimCallStep
              linkedCoverage={linkedCoverage}
              matterId={matterId}
              callStatus={callStatus}
              isCallOutcomeReady={isCallOutcomeReady}
              onRetryCall={handleTriggerCall}
              onSendToBpo={handleTriggerBpo}
              onBackToReview={() => handleGoto('review')}
              isStartingCall={startWorkflowState.isLoading}
              startCallError={
                (startWorkflowState.error as { data?: { error?: string } })?.data?.error || null
              }
              key={`call-step-${refreshTick}`}
            />
          ))}

        {step === 'lor' && (
          <ClaimLorStep
            matterId={matterId}
            coverageId={coverageIdParam}
            setActiveFieldKey={setActiveFieldKey}
            finalizeDetails={finalizeDetails}
            updateFinalizeDetail={updateFinalizeDetail}
            saveFinalizeDetails={saveFinalizeDetails}
            activeFaxTransmissions={activeFaxTransmissions}
            onSendFax={handleFaxLor}
            onTransmissionComplete={(lorId: string) => {
              // Optionally clear the transmission from tracking after completion
              // eslint-disable-next-line no-console
              console.log(`Fax transmission completed for LoR ${lorId}`);
            }}
            isSendingFax={isSendingFax}
          />
        )}

        {step === 'review_send' && <ReviewSendLorPage hideHeader />}

        {step === 'ack' && (
          <div className="p-4">
            <ClaimAcknowledgementStep
              matterId={matterId}
              coverageId={coverageIdParam}
            />
          </div>
        )}
      </>
    );
  })();

  return (
    <AgentChatProvider>
      <div className="flex flex-col min-h-screen w-full">
        <ClaimFilingTopHeader matterId={matterId} />

        <div className="flex-1 bg-gray-50 flex flex-col">
          {workflowStepper}

          {/* Scrollable main content area; keep footer sticky below */}
          <div className="flex-1 overflow-auto max-w-7xl mx-auto justify-center">
            {workflowContent}
          </div>
        </div>

        <Dialog open={isConfirmModalOpen} onOpenChange={setIsConfirmModalOpen}>
          <DialogContent className={isBpoMode ? 'sm:max-w-2xl' : 'sm:max-w-md'}>
            <DialogHeader>
              <DialogTitle>
                {isBpoMode ? 'Send to BPO' : 'Confirm phone number'}
              </DialogTitle>
              <DialogDescription>
                {isBpoMode
                  ? "We'll start a BPO call to the insurance carrier. You can edit the number and optionally attach files to the Slack message."
                  : "We'll start a Voice AI call to the insurance carrier. You can edit the number before continuing."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <label
                htmlFor="proposed_number"
                className="block text-sm text-gray-700"
              >
                <span className="block mb-1">Phone number</span>
                <input
                  id="proposed_number"
                  value={proposedNumber}
                  onChange={(e) => setProposedNumber(e.target.value)}
                  aria-label="Phone number to call"
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  placeholder="+1 (555) 123-4567"
                />
              </label>

              {isBpoMode && (
                <div className="border-t pt-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Attach Files (Optional)
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Select documents to attach to the BPO Slack message.
                  </p>
                  <div className="max-h-64 overflow-y-auto overflow-x-hidden border rounded-md p-2 bg-gray-50">
                    <DocumentSelectionWithUpload
                      matterId={matterId}
                      selectedBlobIds={bpoAttachmentBlobIds}
                      onSelectionChange={setBpoAttachmentBlobIds}
                      title=""
                      description=""
                    />
                  </div>
                  {bpoAttachmentBlobIds.size > 0 && (
                    <p className="text-xs text-gray-600 mt-2">
                      {bpoAttachmentBlobIds.size} file
                      {bpoAttachmentBlobIds.size !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setIsConfirmModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleConfirmStart}>
                  {isBpoMode ? 'Send to BPO' : 'Start Voice AI Call'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AgentChatProvider>
  );
};
